// import qs from 'qs'
// import { logger } from '../common'

import { logger } from '../common';
import localStorage from './storage/localStorage';
import sessionStorage from './storage/sessionStorage';

// 定位坐标兜底值（成都）：旅游这些接口对 lat、lng 做非空校验，
// 定位没拿到时传空串会被后端直接打回（code 10002「lat 不能为空」）
export const DEFAULT_LAT = '30.5702';
export const DEFAULT_LNG = '104.06476';

/**
 * 取当前可用于接口的经纬度（取值顺序对齐 mall_h5 的 utils.getLocationCityInfo）：
 * 传入的城市信息 → 本地保存的定位城市 → 最近一次定位（Header 定位成功会写 sessionStorage）
 * → 兜底坐标。任何一环都没有时也不会返回空串。
 * @param {object} cityInfo 当前城市（vuex common/currentCity 这类，可能只有 id/名字、没有坐标）
 * @returns {{lat: string, lng: string}}
 */
export function getLatLng(cityInfo = {}) {
  const { lat: cityLat, lng: cityLng } = cityInfo || {};
  const last = localStorage.get('lastLocationCityInfo') || {};
  const lat = cityLat || last.lat || sessionStorage.get('lat') || DEFAULT_LAT;
  const lng = cityLng || last.lng || sessionStorage.get('lng') || DEFAULT_LNG;
  return { lat: String(lat), lng: String(lng) };
}

/**
 * 金额展示格式化：**只用于最终呈现，返回值不要再参与计算**
 *
 * 口径对齐 mall_h5 的 utils/payment/format.js#formatMoney：
 *   - 只保留两位小数、截断（不四舍五入，避免把价格显示得比实际高）
 *   - 去掉末尾多余的 0 与小数点：29.90 → 29.9、30.00 → 30
 *   - 非法值（null / undefined / '' / NaN / Infinity）统一返回 '0'
 * 优化点：h5 原实现直接 Math.floor(value * 100)，遇到 29.9 这种
 *   （29.9 * 100 = 2989.9999999999995）会截成 29.89；这里先按 1e-6 修正浮点误差再截断。
 *
 * 全局可用：main.js 里挂在 Vue.prototype.$formatMoney，模板里直接 {{ $formatMoney(price) }}
 * @param {number|string} value 金额
 * @param {number} [decimalPlaces] 传 2 时强制保留两位小数（合计 / 费用这类要对齐的场景）
 * @returns {string}
 */
export function formatMoney(value, decimalPlaces) {
  const fixed =
    decimalPlaces || decimalPlaces === 0 ? Number(decimalPlaces) : null;
  const fallback = fixed ? (0).toFixed(fixed) : '0';
  if (value === null || value === undefined || value === '') return fallback;
  const num = Number(value);
  if (isNaN(num) || !isFinite(num)) return fallback;
  const negative = num < 0;
  // 先修正浮点误差再做整数截断（1e-6 精度足够覆盖两位小数的场景）
  const cents = Math.floor(Number((Math.abs(num) * 100).toFixed(6)));
  let res;
  if (fixed !== null) {
    res = (cents / 100).toFixed(fixed);
  } else {
    res = (cents / 100)
      .toFixed(2)
      .replace(/(\.\d*[1-9])0+$/, '$1') // 29.90 → 29.9
      .replace(/\.0+$/, ''); // 30.00 → 30
  }
  return negative ? `-${res}` : res;
}

export function importAllImage(r) {
  const keys = r.keys();
  const data = keys.map(r);
  const tmp = {};
  keys.forEach((item, idx) => {
    const rename = item.replace(/(^.*\/)|(\.png)|(\.gif)|(\.jpe?g)/gi, '');
    tmp[rename] = data[idx];
  });
  return tmp;
}

/**
 * 加载JavaScript
 *
 * @param {string} src
 * @param {function} cb
 */
export function loadScript(src, cb) {
  let s;
  let t;
  let first = false;
  s = document.createElement('script');
  s.type = 'text/javascript';
  s.src = src;
  s.onload = s.onreadystatechange = function() {
    if (!first && (this.readyState === 'complete' || !this.readyState)) {
      first = true;
      cb();
    }
  };
  t = document.getElementsByTagName('script')[0];
  t.parentNode.insertBefore(s, t);
}

// 加载外部css
export function loadCss(url) {
  let css = document.createElement('link');
  css.href = url;
  css.rel = 'stylesheet';
  css.type = 'text/css';
  document.head.appendChild(css);
}

/**
 * 是否为微信开发者工具
 */
export function isWxDevTools() {
  if (/wechatdevtools/gi.test(navigator.userAgent)) {
    return true;
  }
  return false;
}

// 判断是否是微信
export function isWechat() {
  var ua = navigator.userAgent.toLowerCase();
  return ua.indexOf('micromessenger') != -1;
}

// 判断用户环境[移动端/PC]
export function getEnvironment() {
  if (/Android|webOS|iPhone|iPod|BlackBerry/i.test(navigator.userAgent)) {
    // 移动端打开
    return 1;
  } else {
    // pc端打开的
    return 2;
  }
}

export function compareVersion(v1, v2) {
  v1 = v1.split('.');
  v2 = v2.split('.');
  const len = Math.max(v1.length, v2.length);

  while (v1.length < len) {
    v1.push('0');
  }
  while (v2.length < len) {
    v2.push('0');
  }

  for (let i = 0; i < len; i++) {
    const num1 = parseInt(v1[i]);
    const num2 = parseInt(v2[i]);

    if (num1 > num2) {
      return 1;
    } else if (num1 < num2) {
      return -1;
    }
  }

  return 0;
}

// 节流
export function throttle(fn, delay) {
  var prev = Date.now();
  return function() {
    var now = Date.now();
    if (now - prev > delay) {
      fn();
      prev = Date.now();
    }
  };
}

export function query2Obj(param) {
  const pattern = /([^?&=]+)=([^&#]*)/g;
  const dict = {};
  let search = null;
  if (typeof param === 'object' && param instanceof Location) {
    search = param.search;
  } else if (typeof param === 'string') {
    search = param;
  } else {
    throw new Error('参数类型非法！请传入window.loaction对象或者url字符串。');
  }
  search.replace(pattern, (rs, $1, $2) => {
    const key = decodeURIComponent($1);
    const value = decodeURIComponent($2);
    dict[key] = value;
    return rs;
  });
  return dict;
}

export function tryCatch(fn, failCallback) {
  try {
    if (typeof fn === 'function') {
      fn();
    }
  } catch (err) {
    if (typeof failCallback === 'function') {
      failCallback();
    }
    logger(err);
  }
}
/**
 * js精度计算
 * 获取需要将两个数据转换为最大整数的最大10阶层的位数
 * @param {string} num1
 * @param {string} num2
 */
export const getPrecisionLength = (num1, num2) => {
  if (!(Number(num1) && Number(num2))) return 0;
  let len1,
    len2 = 0;
  try {
    len1 = num1.split('.')[1].length;
  } catch (e) {
    logger(e);
  }
  try {
    len2 = num2.split('.')[1].length;
  } catch (e) {
    logger(e);
  }
  return len1 > len2 ? len1 : len2;
};

export function getDataType(item) {
  let typeStr = Object.prototype.toString.call(item);
  let type = '';
  if (typeStr === '[object Number]') {
    type = 'Number';
  } else if (typeStr === '[object String]') {
    type = 'String';
  } else if (typeStr === '[object Object]') {
    type = 'Object';
  } else if (typeStr === '[object Array]') {
    type = 'Array';
  } else if (typeStr === '[object Boolean]') {
    type = 'Boolean';
  } else if (typeStr === '[object Function]') {
    type = 'Function';
  } else if (typeStr === '[object Null]') {
    type = 'Null';
  } else if (typeStr === '[object Undefined]') {
    type = 'Undefined';
  } else if (typeStr === '[object Symbol]') {
    type = 'Symbol';
  } else {
    type = 'Unknown';
  }
  return type;
}

/**
 * 3m 域名列表
 */
const _3M_HOSTS = [
  '3m.pandayoufu.com',
  '3m2025.pandayoufu.com',
  '3m2025-test.pandayoufu.com'
];
const _TAITAN_HOSTS = ['taitan.pandayoufu.com'];
/**
 * 判断当前 host 是否是 3m 域名
 * @param {string} host
 * @returns {boolean}
 */
export function is3mHost(host = window.location.host) {
  return (
    // _3M_HOSTS.indexOf(host) !== -1 || process.env.NODE_ENV === 'development'
    _3M_HOSTS.indexOf(host) !== -1
  );
}

/**
 * 判断当前 host 是否是 taitan 域名
 * @param {string} host
 * @returns {boolean}
 */
export function isTaitanHost(host = window.location.host) {
  return (
    // _TAITAN_HOSTS.indexOf(host) !== -1 || process.env.NODE_ENV === 'development'
    _TAITAN_HOSTS.indexOf(host) !== -1
  );
}
/**
 * 判断当前环境是否是 3m（包含开发环境）
 * @returns {boolean}
 */
export function is3mEnv() {
  // return is3mHost() || process.env.NODE_ENV === 'development';
  return is3mHost();
}

/**
 *
 * @returns 是否是pc环境的浏览器
 */
export function isPCBrowser() {
  const userAgent = navigator.userAgent;
  const mobileKeywords = [
    'Android',
    'iPhone',
    'iPad',
    'Windows Phone',
    'Mobile'
  ];

  // 如果包含移动设备关键词，则不是PC浏览器
  for (let keyword of mobileKeywords) {
    if (userAgent.includes(keyword)) {
      return false;
    }
  }

  // 否则认为是PC浏览器
  return true;
}
